import { useCallback, useState } from "react";

const STORAGE_KEY = "staff-ui:sidebar-favourites";

/**
 * Favourites are stored as route paths, never labels: labels get reworded,
 * routes are stable. Anything unreadable or malformed is treated as "no
 * favourites" rather than throwing — blocked or full storage must not be able
 * to break the shell.
 */
function readStored(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
}

function writeStored(paths: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(paths));
  } catch {
    // Best-effort; the favourites still work for this session.
  }
}

/**
 * The favourited route paths, oldest first.
 *
 * Deliberately returns paths and nothing else: what a path *means* — whether it
 * still exists, and whether this user may see it — is resolved against the
 * gated nav by the caller on every render, so storage never decides what is
 * visible.
 */
export function useFavourites(): {
  favouritePaths: string[];
  toggleFavourite: (path: string) => void;
} {
  const [favouritePaths, setFavouritePaths] = useState<string[]>(readStored);

  const toggleFavourite = useCallback((path: string) => {
    setFavouritePaths(prev => {
      // Newest last, so the group reads in the order things were added.
      const next = prev.includes(path) ? prev.filter(entry => entry !== path) : [...prev, path];
      writeStored(next);
      return next;
    });
  }, []);

  return { favouritePaths, toggleFavourite };
}
